import { sydneyStartOfDay, shiftSydneyDay, sydneyToday, sydneyDayOf } from "@artemis-group/aiw-contracts";
// Lead inbox (signed language): every lead the second it arrives. Source and
// cost are never blank; whose turn · source · cost · clock sit together on the
// row. A row opens the drawer; ?new=1 opens the phone / walk-in form.
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, cn } from "@aiw/ui";
import { Page } from "../shell/Page";
import { useLead } from "../hooks/useReads";
import { ListMore, useNewestList } from "../components/newest-list";
import { useMe } from "../hooks/useMe";
import { useNow, useProposal, type Lead } from "../hooks/slice-a";
import { SOURCE_LABEL, aud, day, sourceLabel, time } from "../lib/format";
import { Chip, OutcomePill, Pill, ProposalToast, Turn, firstName, selectCls } from "../components/job-bits";
import { LeadDrawer, costWords, dispositionWords, isOpen, leadName, leadOwner } from "../components/lead-drawer";
import { CATEGORIES, LeadNewForm } from "../components/lead-new-form";

type Tab = "all" | "accepted" | "mine" | "closed";
type Range = "7" | "today" | "30" | "all";
const OUTCOMES = ["unworked", "contacted", "won", "lost", "dead", "stalled", "completed"];

// Ranges start at Sydney midnight, not the device's (audit #264).
const sinceFor = (r: Range): string | undefined => {
  if (r === "all") return undefined;
  const back = r === "7" ? 7 : r === "30" ? 30 : 0;
  return sydneyStartOfDay(shiftSydneyDay(sydneyToday(), -back)) ?? undefined;
};

export function Leads() {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const myName = firstName(me.data?.user.email);
  const now = useNow(1000);
  const propose = useProposal();

  const [tab, setTab] = useState<Tab>("accepted");
  const [source, setSource] = useState("");
  const [range, setRange] = useState<Range>("all");
  const [category, setCategory] = useState("");
  const [outcome, setOutcome] = useState("");
  const [openId, setOpenId] = useState<string | null>(params.get("lead"));

  const since = useMemo(() => sinceFor(range), [range]);
  // Newest received first, one page at a time. The tabs, their counts and the filters are decided by the API
  // over the whole history (crm-list-page.ts) — a page held here cannot count a tab.
  const list = useNewestList<Lead, { counts?: Record<Tab, number>; categories?: string[] }>("/api/leads", { location: "all", tab, owner: myName || undefined, channel: source || undefined, since, category: category || undefined, outcome: outcome || undefined });
  const q = list.query;
  const rows = list.rows.map(l => ({ ...l, owner: l.channel_signals?.response?.owner_name ?? l.owner }));
  // A ?lead= link may name a lead older than the loaded page: read that one lead directly.
  const linked = useLead(openId && !rows.some((l) => l.id === openId) ? openId : "");
  const open = openId ? rows.find((l) => l.id === openId) ?? (linked.data as unknown as Lead | undefined) ?? null : null;

  const closeNew = () => { params.delete("new"); setParams(params, { replace: true }); };

  return (
    <Page
      title="Lead inbox"
      lede="Current enquiries needing attention. Older imports and closed records remain in All history; quoted and stalled work is in Pipeline."
      actions={<>
        <Button asChild size="sm"><Link to="/leads?new=1">+ Log a phone / walk-in lead</Link></Button>
      </>}
    >
      <div className="mb-3 flex flex-wrap gap-1 border-b" role="tablist">
        {([["accepted", "Active leads"], ["mine", "Assigned to me"], ["all", "All history"], ["closed", "Closed this week"]] as [Tab, string][]).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={cn("-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm", tab === id ? "border-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {label}{list.first?.counts && <span className={cn("rounded-full px-1.5 font-mono text-micro leading-4", tab === id ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground")}>{list.first.counts[id] ?? 0}</span>}
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={selectCls} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source"><option value="">All sources</option>{Object.entries(SOURCE_LABEL).map(([k, w]) => <option key={k} value={k}>{w}</option>)}</select>
        <select className={selectCls} value={range} onChange={(e) => setRange(e.target.value as Range)} aria-label="Received"><option value="7">Last 7 days</option><option value="today">Today</option><option value="30">Last 30 days</option><option value="all">All time</option></select>
        <select className={selectCls} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category"><option value="">Any category</option>{[...new Set([...CATEGORIES, ...(list.first?.categories ?? [])])].map((c) => <option key={c}>{c}</option>)}</select>
        <select className={selectCls} value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="Outcome"><option value="">Any outcome</option>{OUTCOMES.map((o) => <option key={o}>{o}</option>)}</select>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
            <tr>
              <th className="hidden px-3 py-2 font-medium md:table-cell">Received</th>
              <th className="px-3 py-2 font-medium">Lead · category · brief</th>
              <th className="hidden px-3 py-2 font-medium md:table-cell">Owner · source · cost</th>
              <th className="hidden px-3 py-2 font-medium md:table-cell">Disposition</th>
              <th className="hidden px-3 py-2 font-medium md:table-cell">Outcome</th>
              <th className="hidden px-3 py-2 font-medium xl:table-cell">Job</th>
              <th className="hidden px-3 py-2 text-right font-medium xl:table-cell">Deal value</th>
            </tr>
          </thead>
          <tbody>
            {q.isPending && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Loading leads…</td></tr>}
            {q.isError && <tr><td colSpan={7} role="alert" className="px-3 py-8 text-center text-warning">Leads could not be loaded. This is not an empty inbox. {q.error.message}</td></tr>}
            {q.isSuccess && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">{"No leads in this view."}</td></tr>}
            {rows.map((l) => {
              const received = new Date(l.received_at);
              const today = sydneyDayOf(l.received_at) === sydneyToday();
              return (
                <tr key={l.id} onClick={() => setOpenId(l.id)} className={cn("cursor-pointer border-t hover:bg-muted/50", openId === l.id && "bg-accent/40", l.data_origin === "test" && "bg-warning/5")}>
                  <td className={cn("hidden border-l-4 px-3 py-2 align-top md:table-cell", isOpen(l) ? "border-l-primary" : l.disposition === "went_quiet" ? "border-l-warning" : "border-l-transparent")}>
                    <span className="num text-micro">{today ? time(l.received_at) : day(l.received_at)}</span>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium">{leadName(l)}{l.data_origin === "test" && <Pill tone="warn" className="ml-1.5">test</Pill>}</div>
                    <div className="text-micro text-muted-foreground">{[l.suburb, l.postcode].filter(Boolean).join(" ") || "no suburb"} · <Chip>{l.category ?? "no category"}</Chip></div>
                    {l.raw_brief && <div className="line-clamp-3 text-micro text-muted-foreground">“{l.raw_brief.replaceAll("[spacer]", " ")}”</div>}
                    <div className="mt-2 space-y-1 md:hidden">
                      <div className="text-micro text-muted-foreground">{day(l.received_at)} · {sourceLabel(l.source_id || "none")} · {costWords(l)}</div>
                      <Pill>{dispositionWords(l.disposition)}</Pill>
                      {l.disposition_reason && <div className="text-micro text-muted-foreground">{l.disposition_reason}</div>}
                      {l.job_ref && <Link className="block text-micro text-primary" to={`/jobs/${l.job_ref}`} onClick={e=>e.stopPropagation()}>Open work {l.job_ref} →</Link>}
                    </div>
                  </td>
                  <td className="hidden px-3 py-2 align-top md:table-cell">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Turn owner={leadOwner(l)} quiet={!isOpen(l)} />
                      <Chip>{sourceLabel(l.source_id || "none")}{l.facet ? ` · ${l.facet}` : ""}</Chip>
                      <span className="num text-micro text-gold">{costWords(l)}</span>
                    </div>
                  </td>
                  <td className="hidden px-3 py-2 align-top md:table-cell">
                    <Pill tone={isOpen(l) ? "plain" : l.disposition === "went_quiet" ? "warn" : "info"}>{dispositionWords(l.disposition)}</Pill>
                    {l.disposition_reason && <div className="text-micro text-muted-foreground">{l.disposition_reason}</div>}
                    {l.disposition_at && <div className="num text-micro text-muted-foreground">{day(l.disposition_at)} {time(l.disposition_at)}</div>}
                  </td>
                  <td className="hidden px-3 py-2 align-top md:table-cell"><OutcomePill outcome={l.outcome} /></td>
                  <td className="hidden px-3 py-2 align-top font-mono text-micro xl:table-cell">{l.job_ref ? <Link className="text-primary" to={`/jobs/${l.job_ref}`} onClick={(e) => e.stopPropagation()}>{l.job_ref}</Link> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="num hidden px-3 py-2 text-right align-top text-gold xl:table-cell">{l.deal_value != null ? aud(l.deal_value) : <span className="text-muted-foreground">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ListMore list={list} />

      <LeadDrawer lead={open} now={now} me={myName || "Artemis"} propose={propose} onClose={() => setOpenId(null)} />
      <LeadNewForm open={params.get("new") === "1"} propose={propose} onClose={closeNew} />
      <ProposalToast note={propose.note} onClose={propose.clear} />
    </Page>
  );
}
